import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import styles from "./Toast.module.css";
import { useMediaQuery } from "@mui/material";
import { CheckCircleFilled, Warning, Delete } from "@ippon/shared-assets";
import { useNavigate } from "react-router-dom";
import { LinkIcon } from "@/common/icons";

export interface ToastProps {
  message: string;
  secondMessage?: string;
  type: "success" | "error" | "warning" | "info" | "deleted";
  linkId?: string;
  linkName?: string;
  onClose: () => void;
  LinkIcon?: React.FC<any>;
  SuccessIcon?: React.ReactNode;
  ErrorIcon?: React.ReactNode;
  DeletedIcon?: React.ReactNode;
}

const DefaultSuccessIcon = () => <CheckCircleFilled />;
const DefaultErrorIcon = () => <Warning />;
const DefaultDeletedIcon = () => <Delete />;

const Icon: React.FC<{
  type: ToastProps["type"];
  SuccessIcon?: React.ReactNode;
  ErrorIcon?: React.ReactNode;
  DeletedIcon?: React.ReactNode;
}> = ({ type, SuccessIcon, ErrorIcon, DeletedIcon }) => {
  if (type === "success")
    return (
      <span className={styles.icon}>
        {SuccessIcon ?? <DefaultSuccessIcon />}
      </span>
    );
  if (type === "error")
    return (
      <span className={styles.icon}>{ErrorIcon ?? <DefaultErrorIcon />}</span>
    );
  if (type === "deleted")
    return (
      <span className={styles.icon}>
        {DeletedIcon ?? <DefaultDeletedIcon />}
      </span>
    );
  return (
    <span className={styles.icon}>
      <PriorityHighIcon />
    </span>
  );
};

export const Toast: React.FC<ToastProps> = ({
  message,
  secondMessage = "",
  type,
  linkId = "",
  linkName = "",
  onClose,
  LinkIcon: LinkIconComp,
  SuccessIcon,
  ErrorIcon,
  DeletedIcon,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();

  const triggerClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      triggerClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const detailHandler = () => {
    if (linkId) {
      navigate(`/properties/${linkId}`);
      onClose();
    }
  };

  return (
    <div
      className={[
        styles.toastMessage,
        isMobile ? styles.toastMessageSp : "",
        isClosing ? styles.fadeOut : styles.fadeIn,
        styles[type],
      ].join(" ")}
    >
      <div
        className={[
          styles.topLineWrapper,
          linkName !== "" ? styles.topLineWrapperIsLink : "",
        ].join(" ")}
      >
        <div className={styles.messageWrapper}>
          <Icon
            type={type}
            SuccessIcon={SuccessIcon}
            ErrorIcon={ErrorIcon}
            DeletedIcon={DeletedIcon}
          />
          <p
            className={[styles.message, isMobile ? styles.messageSp : ""].join(
              " "
            )}
            style={{ margin: 0, fontWeight: 700 }}
          >
            {message}
            {secondMessage !== "" && (
              <>
                <br />
                {secondMessage}
              </>
            )}
          </p>
        </div>
        <button
          className={[
            styles.closeButton,
            isMobile ? styles.closeButtonSp : "",
          ].join(" ")}
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </div>
      {linkId && linkName && (
        <div className={styles.bottomWrapper}>
          <div className={styles.linkName}>
            物件名:
            <p className={styles.linkText} onClick={detailHandler}>
              {linkName}
              <span style={{ marginLeft: 5 }} className="linkIcon">
                {LinkIconComp ? <LinkIconComp /> : <LinkIcon />}
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Toast;
